import { useState } from 'react';
import { Sparkles, AlertTriangle, AlertOctagon, Info } from 'lucide-react';
import {
  analyzeAnalytics,
  type AnalyticsAnalystReport,
  type AnalyticsFilter,
  type AnalyticsInsight,
} from '../../services/api/analytics';

interface Props {
  filter: AnalyticsFilter;
  periodLabel: string;
}

const severityStyle: Record<
  AnalyticsInsight['severity'],
  { bg: string; border: string; text: string; Icon: typeof Info }
> = {
  info: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-900',
    Icon: Info,
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-900',
    Icon: AlertTriangle,
  },
  alert: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-900',
    Icon: AlertOctagon,
  },
};

const categoryLabel: Record<AnalyticsInsight['category'], string> = {
  volume: 'Volym',
  quality: 'Kvalitet',
  intent: 'Intent',
  cost: 'Kostnad',
  other: 'Övrigt',
};

export const AnalyticsAnalystPanel = ({ filter, periodLabel }: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<AnalyticsAnalystReport | null>(null);

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyzeAnalytics(filter);
      setReport(res.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Okänt fel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Sparkles className="h-4 w-4 text-indigo-500" /> AI-analys av perioden
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Låt LLM granska volym, kvalitet, intent och kostnader för{' '}
            <span className="font-medium text-slate-700">{periodLabel}</span> och föreslå åtgärder.
          </p>
        </div>
        <button
          type="button"
          onClick={runAnalysis}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {loading ? 'Analyserar…' : report ? 'Analysera igen' : 'Kör analys'}
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      {report && !error && (
        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Sammanfattning
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{report.summary}</p>
          </div>

          {report.highlights.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Höjdpunkter
              </div>
              <ul className="space-y-1 text-sm text-slate-700">
                {report.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-slate-400">·</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.insights.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Insikter ({report.insights.length})
              </div>
              <div className="space-y-2">
                {report.insights.map((ins, i) => {
                  const style = severityStyle[ins.severity];
                  return (
                    <div key={i} className={`rounded-lg border ${style.border} ${style.bg} p-3`}>
                      <div className={`flex items-start gap-2 ${style.text}`}>
                        <style.Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">{ins.title}</span>
                            <span className="text-[10px] uppercase tracking-wide rounded-full bg-white/60 px-2 py-0.5 border border-current/20">
                              {categoryLabel[ins.category]}
                            </span>
                          </div>
                          <p className="text-xs leading-relaxed mt-1 opacity-90">
                            {ins.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {report.costBreakdown && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Kostnadsbild
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{report.costBreakdown}</p>
            </div>
          )}

          {report.recommendations.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Rekommendationer
              </div>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {report.recommendations.map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-indigo-500">→</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
            Modell: {report.model} · Analyserad{' '}
            {new Date(report.analyzedAt).toLocaleString('sv-SE')}
          </div>
        </div>
      )}
    </div>
  );
};
